<template>
  <div class="min-h-screen bg-[#f6f7f9] py-10 pb-20">
    <div class="max-w-screen-xl mx-auto px-4">

      <!-- Pagina-header -->
      <div class="mb-8">
        <p class="text-[0.75rem] font-bold text-[#aaa] uppercase tracking-widest mb-1">Hulp & Support</p>
        <h1 class="text-[2rem] font-black text-[#111] leading-tight mb-2">Veelgestelde vragen</h1>
        <p class="text-[#666] text-[0.95rem] max-w-xl">
          Vind snel antwoord op de meest gestelde vragen. Staat je vraag er niet bij?
          <router-link to="/klantenservice" class="text-[#b89b00] font-semibold hover:underline">Neem dan contact op</router-link>.
        </p>
      </div>

      <!-- Zoekbalk -->
      <div class="relative max-w-xl mb-8">
        <i class="bi bi-search absolute left-4 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.9rem]"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Zoek in de FAQ..."
          class="w-full pl-11 pr-10 py-3.5 bg-white border border-[#e5e7eb] rounded-2xl text-[0.9rem] shadow-sm focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all"
        >
        <button v-if="searchQuery" @click="searchQuery = ''" class="absolute right-4 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#555]">
          <i class="bi bi-x text-[1.1rem]"></i>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-24">
        <div class="w-10 h-10 border-4 border-[#f0f0f0] border-t-[#FFD500] rounded-full animate-spin mb-4"></div>
        <p class="text-[#888] text-[0.9rem]">Vragen laden...</p>
      </div>

      <!-- Fout -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
        <i class="bi bi-exclamation-triangle text-red-400 text-[2rem] mb-2 block"></i>
        <p class="text-red-600 font-semibold">FAQ kon niet worden geladen. Probeer het later opnieuw.</p>
      </div>

      <!-- Geen zoekresultaten -->
      <div v-else-if="searchQuery && !filteredGroups.length" class="bg-white rounded-2xl border border-[#e5e7eb] p-10 text-center">
        <i class="bi bi-search text-[2.5rem] text-[#ddd] mb-3 block"></i>
        <p class="text-[#888] font-medium">Geen resultaten voor "<strong>{{ searchQuery }}</strong>"</p>
        <button @click="searchQuery = ''" class="mt-4 text-[0.85rem] text-[#FFD500] font-semibold hover:underline">Zoekopdracht wissen</button>
      </div>

      <!-- Inhoud: sidebar + accordion -->
      <div v-else-if="filteredGroups.length" class="flex gap-8 items-start">

        <!-- ── Sticky sidebar ── -->
        <aside class="hidden lg:block w-64 shrink-0 sticky top-6">
          <div class="bg-white rounded-2xl border border-[#e5e7eb] overflow-hidden shadow-sm">
            <div class="px-5 py-4 border-b border-[#f0f0f0]">
              <p class="text-[0.72rem] font-bold text-[#aaa] uppercase tracking-widest m-0">Categorieën</p>
            </div>
            <nav class="p-2">
              <template v-for="group in filteredGroups" :key="group.id">
                <!-- Parent link -->
                <a
                  :href="`#cat-${group.slug}`"
                  @click.prevent="scrollTo(group.slug)"
                  :class="[
                    'flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-[0.875rem] font-semibold transition-all cursor-pointer no-underline',
                    activeCategory === group.slug
                      ? 'bg-[#FFD500] text-[#111]'
                      : 'text-[#444] hover:bg-[#f8f8f8] hover:text-[#111]'
                  ]"
                >
                  <span>{{ group.name }}</span>
                  <span :class="['text-[0.72rem] font-bold px-2 py-0.5 rounded-full', activeCategory === group.slug ? 'bg-[#111]/10' : 'bg-[#f0f0f0] text-[#888]']">
                    {{ totalItems(group) }}
                  </span>
                </a>
                <!-- Child links (indented) -->
                <template v-if="group.children && group.children.length">
                  <a
                    v-for="child in group.children"
                    :key="child.id"
                    :href="`#cat-${child.slug}`"
                    @click.prevent="scrollTo(child.slug)"
                    :class="[
                      'flex items-center justify-between gap-2 pl-8 pr-4 py-2 rounded-xl text-[0.82rem] font-medium transition-all cursor-pointer no-underline',
                      activeCategory === child.slug
                        ? 'bg-[#FFD500]/30 text-[#111]'
                        : 'text-[#666] hover:bg-[#f8f8f8] hover:text-[#111]'
                    ]"
                  >
                    <span class="flex items-center gap-1.5">
                      <i class="bi bi-chevron-right text-[0.65rem] text-[#ccc]"></i>
                      {{ child.name }}
                    </span>
                    <span class="text-[0.7rem] font-bold px-1.5 py-0.5 rounded-full bg-[#f0f0f0] text-[#aaa]">
                      {{ child.items.length }}
                    </span>
                  </a>
                </template>
              </template>
            </nav>
            <!-- Contact block -->
            <div class="m-3 mt-1 bg-[#fffbeb] border border-[#FFD500]/30 rounded-xl p-4">
              <p class="text-[0.78rem] font-bold text-[#111] mb-1">Vraag niet gevonden?</p>
              <p class="text-[0.75rem] text-[#666] mb-3">Ons team helpt je graag verder.</p>
              <router-link
                to="/klantenservice"
                class="flex items-center gap-1.5 text-[0.78rem] font-bold text-[#111] bg-[#FFD500] px-3 py-2 rounded-lg no-underline hover:bg-[#ffb800] transition-colors"
              >
                <i class="bi bi-chat-dots"></i> Contact opnemen
              </router-link>
            </div>
          </div>
        </aside>

        <!-- ── FAQ accordion secties ── -->
        <div class="flex-1 min-w-0 space-y-10">
          <section
            v-for="group in filteredGroups"
            :key="group.id"
            :id="`cat-${group.slug}`"
            class="scroll-mt-6"
          >
            <!-- Parent sectie-header -->
            <div class="flex items-center gap-3 mb-4">
              <div class="w-8 h-8 bg-[#FFD500] rounded-lg flex items-center justify-center shrink-0">
                <i class="bi bi-patch-question text-[#111] text-[0.85rem]"></i>
              </div>
              <h2 class="text-[1.1rem] font-black text-[#111] m-0">{{ group.name }}</h2>
              <span class="text-[0.72rem] font-bold text-[#aaa] bg-white border border-[#e5e7eb] px-2 py-0.5 rounded-full">
                {{ totalItems(group) }} vragen
              </span>
            </div>

            <!-- Directe items van parent -->
            <div v-if="group.items && group.items.length" class="bg-white rounded-2xl border border-[#e5e7eb] overflow-hidden shadow-sm divide-y divide-[#f0f0f0] mb-6">
              <FaqAccordionItem
                v-for="item in group.items"
                :key="item.id"
                :item="item"
                :open="openItems.has(item.id)"
                :search-query="searchQuery"
                @toggle="toggle(item.id)"
              />
            </div>

            <!-- Child subcategorieën -->
            <template v-if="group.children && group.children.length">
              <div
                v-for="child in group.children"
                :key="child.id"
                :id="`cat-${child.slug}`"
                class="scroll-mt-6 mb-6"
              >
                <!-- Child header -->
                <div class="flex items-center gap-2 mb-3 pl-1">
                  <div class="w-1 h-6 bg-[#FFD500] rounded-full"></div>
                  <h3 class="text-[0.95rem] font-bold text-[#333] m-0">{{ child.name }}</h3>
                  <span class="text-[0.7rem] font-bold text-[#bbb] bg-[#f5f5f5] border border-[#ebebeb] px-2 py-0.5 rounded-full">
                    {{ child.items.length }} vragen
                  </span>
                </div>
                <!-- Child accordion -->
                <div class="bg-white rounded-2xl border border-[#e5e7eb] overflow-hidden shadow-sm divide-y divide-[#f0f0f0]">
                  <FaqAccordionItem
                    v-for="item in child.items"
                    :key="item.id"
                    :item="item"
                    :open="openItems.has(item.id)"
                    :search-query="searchQuery"
                    @toggle="toggle(item.id)"
                  />
                </div>
              </div>
            </template>

          </section>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineComponent, h, Transition } from 'vue'
import { useAnalytics } from '@composables/useAnalytics'

const { trackFaqExpand } = useAnalytics()

// ── Inline accordion-item component ──────────────────────────────────────────
const FaqAccordionItem = defineComponent({
  name: 'FaqAccordionItem',
  props: {
    item:        { type: Object,  required: true },
    open:        { type: Boolean, default: false },
    searchQuery: { type: String,  default: '' },
  },
  emits: ['toggle'],
  setup(props, { emit }) {
    const highlight = (text) => {
      if (!props.searchQuery.trim()) return text
      const q = props.searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return text.replace(new RegExp(`(${q})`, 'gi'), '<mark class="bg-[#FFD500]/40 rounded px-0.5">$1</mark>')
    }
    return () => h('div', { class: 'faq-item' }, [
      h('button', {
        onClick: () => emit('toggle'),
        class: 'w-full flex items-start justify-between gap-4 px-6 py-5 text-left hover:bg-[#fafafa] transition-colors',
      }, [
        h('span', {
          class: `font-semibold text-[0.9rem] leading-snug flex-1 ${props.open ? 'text-[#111]' : 'text-[#333]'}`,
          innerHTML: highlight(props.item.question),
        }),
        h('span', {
          class: `shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all mt-0.5 ${props.open ? 'bg-[#FFD500] rotate-180' : 'bg-[#f0f0f0]'}`,
        }, [
          h('i', { class: `bi bi-chevron-down text-[0.7rem] font-black ${props.open ? 'text-[#111]' : 'text-[#666]'}` }),
        ]),
      ]),
      h(Transition, { name: 'faq-slide' },
        () => props.open
          ? h('div', { class: 'px-6 pb-5' }, [
              h('div', {
                class: 'prose prose-sm text-[0.88rem] text-[#555] leading-relaxed max-w-none border-t border-[#f4f4f4] pt-4 [&>p]:mb-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-3 [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-3 [&>a]:text-[#b89b00] [&>a]:font-semibold [&>strong]:text-[#111]',
                innerHTML: props.item.answer,
              }),
            ])
          : null
      ),
    ])
  },
})

// ── State ─────────────────────────────────────────────────────────────────────
const loading        = ref(true)
const error          = ref(false)
const groups         = ref([])
const searchQuery    = ref('')
const openItems      = ref(new Set())
const activeCategory = ref('')

// ── Helpers ───────────────────────────────────────────────────────────────────
const totalItems = (group) =>
  (group.items?.length || 0) + (group.children || []).reduce((s, c) => s + c.items.length, 0)

const allItemsInGroup = (group) => [
  ...(group.items || []),
  ...(group.children || []).flatMap(c => c.items),
]

// ── Data ophalen ─────────────────────────────────────────────────────────────
const fetchFaq = async () => {
  try {
    const res = await fetch('/_faq')
    if (!res.ok) throw new Error('fetch failed')
    groups.value = await res.json()
    if (groups.value.length) activeCategory.value = groups.value[0].slug
  } catch (e) {
    console.error('FAQ fetch error:', e)
    error.value = true
  } finally {
    loading.value = false
  }
}

// ── Zoeken: filter per groep + children ───────────────────────────────────────
const filteredGroups = computed(() => {
  if (!searchQuery.value.trim()) return groups.value
  const q = searchQuery.value.toLowerCase()
  const matchItem = (i) =>
    i.question.toLowerCase().includes(q) ||
    i.answer.replace(/<[^>]+>/g, '').toLowerCase().includes(q)
  return groups.value
    .map(g => {
      const items = (g.items || []).filter(matchItem)
      const children = (g.children || [])
        .map(c => ({ ...c, items: c.items.filter(matchItem) }))
        .filter(c => c.items.length > 0)
      return { ...g, items, children }
    })
    .filter(g => g.items.length > 0 || g.children.length > 0)
})

// ── Accordion toggle ──────────────────────────────────────────────────────────
const toggle = (id) => {
  const next = new Set(openItems.value)
  const isOpening = !next.has(id)
  isOpening ? next.add(id) : next.delete(id)
  openItems.value = next
  if (isOpening) {
    const question = groups.value.flatMap(g => allItemsInGroup(g)).find(i => i.id === id)
    trackFaqExpand(question?.question || String(id))
  }
}

// ── Scroll + actieve categorie bijhouden ──────────────────────────────────────
const scrollTo = (slug) => {
  const el = document.getElementById(`cat-${slug}`)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  activeCategory.value = slug
}

let observer = null
const setupObserver = () => {
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activeCategory.value = entry.target.id.replace('cat-', '')
        }
      }
    },
    { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
  )
  // Observe both parent and child section anchors
  groups.value.forEach(g => {
    const el = document.getElementById(`cat-${g.slug}`)
    if (el) observer.observe(el)
    ;(g.children || []).forEach(c => {
      const cel = document.getElementById(`cat-${c.slug}`)
      if (cel) observer.observe(cel)
    })
  })
}

onMounted(async () => {
  await fetchFaq()
  // Open eerste item van elke groep (parent + children)
  groups.value.forEach(g => {
    if (g.items?.[0])    openItems.value.add(g.items[0].id)
    ;(g.children || []).forEach(c => { if (c.items?.[0]) openItems.value.add(c.items[0].id) })
  })
  setTimeout(setupObserver, 100)
})

onUnmounted(() => { observer?.disconnect() })
</script>

<style scoped>
.faq-slide-enter-active,
.faq-slide-leave-active {
  transition: max-height 0.28s ease, opacity 0.22s ease;
  overflow: hidden;
  max-height: 600px;
}
.faq-slide-enter-from,
.faq-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.prose :deep(a) { color: #b89b00; }
.prose :deep(strong) { color: #111; }
</style>
